import { type MouseEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from '@cmsgov/design-system';
import { useApplicationQuery, useApplicationsQuery, useMyApplicationsQuery } from '@access-cms/api-client';

import { Button, ButtonVariation } from '../../components/Button';
import DetailsList, { DetailItem } from '../../components/DetailsList/DetailsList';
import { PageLayout } from '../../components/PageLayout';
import { Spinner } from '../../components/Spinner';
import ApplicationPicker from './components/ApplicationPicker';
import ApplicationStatusTracker, { StatusStep } from './components/ApplicationStatusTracker';
import { buildTrackerSteps, type SubStatusRegions, type TrackerState } from './components/FingerprintEappStatus';
import { workflowConfig } from './config/workflow';
import { useApplication } from './context';
import { useApplicationSelection } from './hooks/useApplicationSelection';
import { getFieldIndices } from './utils/fieldToSubStepMapper';
import { type ApplicationListItem, selectApplicationForStatus } from './utils/selectApplicationForStatus';

import './ApplicationStatus.css';

/**
 * Shape of a single returned-field issue (db-core FieldIssue, ACP-1027).
 * `fieldPath` is a dotted path into applicationData (e.g. "personalDetails.email").
 */
interface FieldIssue {
  fieldPath: string;
  fieldLabel: string;
  message: string;
  currentValue?: string | number | boolean | null;
}

/** Reviewer/COR return feedback surfaced for returned applications (db-core ReturnFeedback). */
interface ReturnFeedback {
  fieldIssues?: FieldIssue[];
  generalNotes?: string;
}

/**
 * Sections owned by the COR (not the applicant). Mirrors db-core SECTION_OWNERS;
 * redeclared here because the UI does not depend on db-core. Only contractDetails
 * is COR-owned today.
 */
const COR_OWNED_SECTIONS = new Set(['contractDetails']);

/**
 * Defense in depth (ACP-1040): the backend already redacts COR-owned feedback for
 * the applicant audience, so the contractor only receives their own corrections.
 * This guard mirrors the backend rule (redactReturnFeedbackForApplicant): only
 * VAS-originated issues can be COR-owned, and VAS puts the section key in
 * `fieldLabel` (with `fieldPath` a bare field/section name). COR-initiated
 * RETURN issues have a dotted `fieldPath` + human label and are always
 * applicant-directed — since PR #339 Contract Details fields go through the
 * Request Edit flow — so `contractDetails.*` paths pass through.
 */
function isApplicantFieldIssue(issue: FieldIssue): boolean {
  return !COR_OWNED_SECTIONS.has(issue.fieldLabel) && !COR_OWNED_SECTIONS.has(issue.fieldPath);
}

/**
 * ApplicationStatus Page
 *
 * Displays the current status of a user's application with a visual progress tracker.
 * Shows completed steps, current step, and pending steps with expandable details.
 */
function ApplicationStatus() {
  const baseTestId = 'application-status-page';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { goToStepForCorrection, applicationId } = useApplication();

  // Only an explicit applicationId deep link should bypass the picker.
  // `applicationId` from context survives client-side navigation from
  // /application, but it must not freeze this page to that application or
  // hide the multi-application picker.
  const linkedApplicationId = searchParams.get('applicationId') || '';

  // Two sources, each doing what the other cannot:
  // - the per-contract map (/me) supplies the contracts the picker offers and
  //   the contract / company / labor-category details its labels show;
  // - the read-only list supplies every application record, including the
  //   terminal ones (REJECTED, CANCELLED) that /me omits but whose outcome this
  //   page must still show (ACP-2435).
  const myApplicationQuery = useMyApplicationsQuery({
    enabled: !linkedApplicationId,
  });
  const applicationsQuery = useApplicationsQuery({
    enabled: !linkedApplicationId,
    filters: { limit: 100, page: 1 },
  });
  const listedApplications = (applicationsQuery.data?.data ?? []) as ApplicationListItem[];

  // Offer every contract with something to track: an application in the map,
  // or any submitted record on it (e.g. a rejection on a contract that now
  // appears roster-only). A roster-only contract with nothing behind it is
  // started from the eNOVA page instead.
  const trackableApplications = myApplicationQuery.data?.applications ?? {};
  const { candidateIds, selectContract, selectedContractId } = useApplicationSelection(trackableApplications);
  const myEntry = selectedContractId ? trackableApplications[selectedContractId] : undefined;

  // The picker chooses the contract; selectApplicationForStatus chooses which
  // record on it to track (in-flight first, then the newest non-draft). With no
  // contract in the map at all, the same rule applies across every record, so a
  // user whose only application was rejected still sees that outcome.
  let selectedStatusApplicationId: string | undefined;
  if (selectedContractId) {
    selectedStatusApplicationId =
      selectApplicationForStatus(listedApplications.filter((app) => app.contractId === selectedContractId))?.id ??
      myEntry?.application?.id;
  } else if (candidateIds.length === 0) {
    selectedStatusApplicationId = selectApplicationForStatus(listedApplications)?.id;
  }

  // The picker is authoritative when this is not an explicit deep link. The
  // context id is only a fallback while the map is loading after navigation.
  // Once a contract is selected the context id must not stand in for it: a
  // contract with no application would otherwise keep showing the previous one.
  const applicationIdForStatus =
    linkedApplicationId || selectedStatusApplicationId || (selectedContractId ? '' : applicationId) || '';
  // Only show the picker when the user actually has more than one application
  // to choose between; a single application is auto-selected upstream.
  const showPicker = !linkedApplicationId && Object.keys(myApplicationQuery.data?.applications ?? {}).length > 1;

  // ACP-1041: fetch the real application status. The applicationId comes from the
  // in-app context, or the ?applicationId deep-link param used by the ACP-1020
  // return email, or a record selected from the read-only application list.
  // Self-scoped on the backend (contractor reads own app).
  // includeWorkflowProgress surfaces returnFeedback (COR field-level return
  // comments) for returned applications (ACP-1027).
  const {
    data: applicationDetail,
    isLoading: isStatusLoading,
    isError: isStatusError,
  } = useApplicationQuery(applicationIdForStatus, {
    enabled: !!applicationIdForStatus,
    includeWorkflowProgress: true,
  });
  const isApplicationListLoading =
    !linkedApplicationId && (myApplicationQuery.isLoading || applicationsQuery.isLoading);
  const isApplicationListError = !linkedApplicationId && (!!myApplicationQuery.error || !!applicationsQuery.error);

  // ACP-1041 boundary cast: the generated api type doesn't yet surface
  // applicationStatus/applicationSubStatus/returnFeedback (api-types regen is a
  // build step). Shapes match db-core's Application / ApplicationRegions / ReturnFeedback.
  const detail = applicationDetail as
    | {
        applicationStatus?: string;
        applicationSubStatus?: SubStatusRegions;
        returnFeedback?: ReturnFeedback;
        trackerState?: TrackerState;
      }
    | undefined;

  /**
   * Navigate the applicant straight to a returned field in the workflow. The
   * fieldPath is either a COR row `fieldPath` ("contractDetails.workEmail") or a
   * VAS field name via buildVasReturnFeedback (webhook-handler.service.ts). The
   * mapper resolves the full dotted path first, then the last path segment.
   *
   * FOLLOW-UP (Edit-link reliability): the VAS section/field vocabulary is owned by
   * ServiceNow and does NOT always match our mapper keys — e.g. VAS "companyAddress"
   * vs mapper "company", or section-level returns where `field` is null. Those
   * currently render with no Edit link (safe fallback, never a broken link). To make
   * Edit links reliable we need (1) the authoritative VAS section/field catalog
   * (sample real callbacks via scripts/vas-test-flow.sh) and (2) an explicit
   * VAS-path -> workflow-location map instead of this leaf heuristic.
   *
   * Enters the FULL workflow at the flagged section (goToStepForCorrection,
   * NOT the locked spot-edit mode): a returned application always has to be
   * re-signed and resubmitted, so the applicant must be able to continue
   * forward through Final Review after fixing the field.
   */
  const handleEditField = (fieldPath: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const indices = getFieldIndices(fieldPath, workflowConfig.steps);

    if (!indices) {
      return;
    }

    goToStepForCorrection(indices.stepIndex, indices.subStepIndex);

    navigate('/application');
  };

  // ACP-1027: real reviewer feedback. Each field issue becomes a DetailsList row;
  // an "Edit" link is shown only when the field resolves to a workflow location
  // (see the FOLLOW-UP on handleEditField). Note: VAS-originated returns leave
  // currentValue empty (VAS does not echo the applicant's prior value), so the
  // value line only appears if a future field-level COR return flow provides it.
  const returnFeedback = detail?.returnFeedback;
  const fieldIssueItems: DetailItem[] = (returnFeedback?.fieldIssues ?? [])
    .filter(isApplicantFieldIssue)
    .map((issue) => {
      const canEdit = !!getFieldIndices(issue.fieldPath, workflowConfig.steps);

      return {
        label: issue.fieldLabel,
        links: canEdit
          ? [{ ariaLabel: `Edit ${issue.fieldLabel}`, label: 'Edit', onClick: handleEditField(issue.fieldPath) }]
          : undefined,
        value: (
          <>
            {issue.currentValue !== undefined && issue.currentValue !== null && issue.currentValue !== '' && (
              <div>{String(issue.currentValue)}</div>
            )}
            <div className='ds-u-color--error ds-u-font-size--small ds-u-margin-top--1'>{issue.message}</div>
          </>
        ),
      };
    });

  const hasReturnFeedback = fieldIssueItems.length > 0 || !!returnFeedback?.generalNotes;

  // The applicant can edit + resubmit from any returned variant. VAS/FNMS
  // resubmissions route back through the COR before re-entering the external
  // review, but from the applicant's side the flow is the same: fix and
  // resubmit from the application workflow.
  const EDITABLE_RETURNED_STATUSES = ['RETURNED', 'FNMS_RETURNED', 'VAS_RETURNED'];
  const canEditAndResubmit = EDITABLE_RETURNED_STATUSES.includes(detail?.applicationStatus ?? '');

  const handleGoToApplication = () => {
    const contractId = selectedContractId ?? searchParams.get('contractId');
    navigate(contractId ? `/application?contractId=${contractId}` : '/application');
  };

  const returnFeedbackContent = hasReturnFeedback ? (
    <>
      <p className='ds-u-margin-top--0 ds-u-margin-bottom--2'>
        Your application needs to be updated. It was returned for the following reasons:
      </p>
      {returnFeedback?.generalNotes && <p className='ds-u-margin-bottom--2'>{returnFeedback.generalNotes}</p>}
      {fieldIssueItems.length > 0 && (
        <DetailsList
          headerClassName='ds-h3 ds-u-margin-top--0 ds-u-margin-bottom--2'
          items={fieldIssueItems}
        />
      )}
      {canEditAndResubmit && (
        <div className='ds-u-margin-top--2'>
          <Button
            isAutoWidth
            aria-label='Make corrections and resubmit your application'
            data-testid={`${baseTestId}-make-corrections`}
            variation={ButtonVariation.SOLID}
            onClick={handleGoToApplication}>
            Make corrections
          </Button>
        </div>
      )}
    </>
  ) : null;

  // A single VAS region (fingerprints or eApp) timing out lets the applicant
  // back in the same way a VAS_RETURNED does, but there is no reviewer
  // feedback to show — just a CTA into the workflow. Mirrors the backend's
  // isVasRegionTimeoutResubmittable (application-sub-status-state).
  const isVasTimeoutResubmittable =
    (detail?.applicationStatus === 'VAS_AWAITING_FINGERPRINTS_OR_EAPP' ||
      detail?.applicationStatus === 'VAS_TIMEOUT') &&
    (detail?.applicationSubStatus?.fingerprints === 'timeout' || detail?.applicationSubStatus?.eapp === 'timeout');

  const timeoutResubmitContent = isVasTimeoutResubmittable ? (
    <div className='ds-u-margin-top--2'>
      <Button
        isAutoWidth
        aria-label='Review your application and resubmit'
        data-testid={`${baseTestId}-resubmit-after-timeout`}
        variation={ButtonVariation.SOLID}
        onClick={handleGoToApplication}>
        Continue Application
      </Button>
    </div>
  ) : null;

  // ACP-1040: derive the full 8-step tracker from the real application state.
  // Sequential review steps read applicationStatus; fingerprint/eApp read the
  // parallel applicationSubStatus. While loading / on error / with no id the
  // status is undefined and every step renders neutral.
  //
  // A selected contract with no application record yet (roster-only, never
  // started) has nothing to fetch. Track it as an unsubmitted DRAFT so the
  // applicant sees Onboarding complete and Submit Application needing action.
  const hasNotStartedApplication =
    !!selectedContractId && !applicationIdForStatus && !isApplicationListLoading && !isApplicationListError;
  const baseSteps = buildTrackerSteps(
    detail?.applicationStatus ?? (hasNotStartedApplication ? 'DRAFT' : undefined),
    detail?.applicationSubStatus,
    detail?.trackerState,
  );

  const terminalOutcomeContent = (() => {
    switch (detail?.trackerState?.outcome) {
      case 'REJECTED':
        return {
          heading: 'Application has been declined.',
          text: 'Your application has been declined and will no longer be processed.',
        };
      case 'CANCELLED':
        return {
          heading: 'Application cancelled',
          text: 'Your application was cancelled and is no longer progressing through onboarding.',
        };
      case 'DENIED':
        return {
          heading: 'Application access denied',
          text: 'Your application received a final access-denied decision and is no longer progressing through onboarding.',
        };
      default:
        return null;
    }
  })();

  const terminalOutcomeAlert = terminalOutcomeContent ? (
    <Alert
      data-testid={`${baseTestId}-terminal-outcome`}
      heading={terminalOutcomeContent.heading}
      variation='error'>
      <p className='ds-u-margin-bottom--0'>{terminalOutcomeContent.text}</p>
    </Alert>
  ) : undefined;

  // Show the reviewer's field feedback inside the step that needs attention, so
  // the badge and the "what to fix" details live together.
  const attentionStepId = baseSteps.find((step) => step.badgeVariant === 'action-needed')?.id;
  const trackerSteps: StatusStep[] = baseSteps.map((step) => {
    if (returnFeedbackContent && step.id === attentionStepId) {
      return {
        ...step,
        content:
          detail?.applicationStatus === 'RETURNED' ? (
            <div>{returnFeedbackContent}</div>
          ) : (
            <div>
              {step.content}
              {returnFeedbackContent}
            </div>
          ),
      };
    }

    // Each timed-out region gets its own CTA, independent of attentionStepId
    // (fingerprints and eApp can time out independently of one another).
    const isTimedOutRegionStep =
      (step.id === 'step-fingerprints' && detail?.applicationSubStatus?.fingerprints === 'timeout') ||
      (step.id === 'step-eapp' && detail?.applicationSubStatus?.eapp === 'timeout');

    if (timeoutResubmitContent && isTimedOutRegionStep) {
      return {
        ...step,
        content: (
          <div>
            {step.content}
            {timeoutResubmitContent}
          </div>
        ),
      };
    }

    return step;
  });

  return (
    <PageLayout
      alert={terminalOutcomeAlert}
      className={terminalOutcomeAlert ? 'application-status-page--with-terminal-outcome' : ''}
      data-testid={baseTestId}
      mainAriaLabelledby='page-heading'
      mainId='content'
      mainTabIndex={-1}>
      <div className='application-status-page__intro'>
        <h1
          className='application-status-page__title'
          id='page-heading'>
          Application Status
        </h1>
        <p className='application-status-page__intro-text'>
          Once the onboarding process begins, you can see which step you are on this page. You can view this page at any
          time to track your status. While your application is being reviewed, you will not be able to make edits. You
          may edit your application only if a reviewer returns it to you.
        </p>
      </div>

      <section
        aria-labelledby='application-status-help-heading'
        className='application-status-page__help ds-u-border--1 ds-u-radius--default ds-u-padding--2'
        data-testid={`${baseTestId}-help`}>
        <h2
          className='application-status-page__help-title'
          id='application-status-help-heading'>
          Have Questions or Need Help?
        </h2>
        <p className='ds-u-margin--0'>
          If you have any questions, contact the CMS IT Service Desk at&nbsp;<strong>1-800-562-1963</strong>
          &nbsp;or&nbsp;
          <strong>410-786-2580</strong>, or email&nbsp;
          <a href='mailto:CMS_IT_Service_Desk@cms.hhs.gov'>CMS_IT_Service_Desk@cms.hhs.gov</a>.
        </p>
      </section>

      <section
        aria-labelledby='tracker-heading'
        className='ds-u-margin-top--5'
        data-testid={`${baseTestId}-tracker-section`}>
        <h2
          className='application-status-page__tracker-title'
          data-testid={`${baseTestId}-tracker-title`}
          id='tracker-heading'>
          Where am I in the Application Process?
        </h2>
        <p className='application-status-page__tracker-copy application-status-page__tracker-description'>
          See where you are in the application process below. You may open each drawer to view more details about each
          step. This information will tell you what to expect as you go through the onboarding process.
        </p>

        {showPicker && (
          <ApplicationPicker
            applications={trackableApplications}
            data-testid={`${baseTestId}-picker`}
            selectedContractId={selectedContractId}
            onSelect={selectContract}
          />
        )}

        <ul className='application-status-page__tracker-copy ds-u-margin-top--2 ds-u-margin-bottom--3'>
          <li>Completed steps will be labeled “Complete” and will display a green check mark.</li>
          <li>The step you are currently on will be labeled “Current Step” and will display a blue location marker.</li>
          <li>
            If you must return to a previous step, you will see an “Action Needed” label with an alert symbol next to it
            and a red arrow icon at that step.
          </li>
        </ul>

        <ApplicationStatusTracker
          data-testid={`${baseTestId}-tracker`}
          steps={trackerSteps}
        />

        {(isStatusLoading || isApplicationListLoading) && (
          <div
            className='ds-u-display--flex ds-u-align-items--center ds-u-margin-top--2'
            data-testid={`${baseTestId}-substatus-loading`}>
            <Spinner
              ariaLabel='Loading your application status'
              data-testid={`${baseTestId}-substatus-spinner`}
            />
            <span className='ds-u-margin-left--2'>Loading your application status&hellip;</span>
          </div>
        )}

        {(isStatusError || isApplicationListError) && (
          <Alert
            className='ds-u-margin-top--2'
            data-testid={`${baseTestId}-substatus-error`}
            heading='Unable to load your application status'
            variation='error'>
            <p className='ds-u-margin-bottom--0'>
              We couldn&apos;t load your application status. Please refresh the page or try again later.
            </p>
          </Alert>
        )}
      </section>
    </PageLayout>
  );
}

export default ApplicationStatus;
